import { useCallback, useMemo, useState } from 'react';
import { MyProfileField } from '../state';
import { BottomSheet } from '@betty/navigation';

export function useMyProfileForm(
  initialFieldData: MyProfileField[],
  handleServiceCall: (fieldData: MyProfileField[]) => Promise<void>,
) {
  const [fieldData, setFieldData] = useState(initialFieldData);
  const [isSubmiting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const toggleEdit = useCallback(() => setIsEditing((prev) => !prev), []);

  const onSubmitForm = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await handleServiceCall(fieldData);
    } catch {
      setFieldData(initialFieldData);
      BottomSheet.open({
        namespace: 'common',
        type: 'Info',
        titleNS: 'account',
        titleKey: 'myProfileErrorTitle',
        data: {
          textNS: 'account',
          textKey: 'myProfileErrorMessage',
        },
      });
    } finally {
      setIsEditing(false);
      setIsSubmitting(false);
    }
  }, [
    fieldData,
    setIsSubmitting,
    setFieldData,
    initialFieldData,
    handleServiceCall,
  ]);

  const onChange = useCallback(
    (fieldKey: MyProfileField['locKey'], newValue: MyProfileField['value']) =>
      setFieldData((prev) =>
        prev.map((field) =>
          field.locKey === fieldKey ? { ...field, value: newValue } : field,
        ),
      ),
    [],
  );

  const returnObj = useMemo(
    () => ({
      fieldData,
      onChange,
      onSubmitForm,
      isSubmiting,
      isEditing,
      toggleEdit,
    }),
    [fieldData, onChange, onSubmitForm, isSubmiting, isEditing, toggleEdit],
  );

  return returnObj;
}
